import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { VigilanceTokenClient } from "@/Common/Api/VigilanceSante/VigilanceTokenClient";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { GeneralInstructionsComponent } from "@/Common/Components/Patient/Prescription/GeneralInstructionsComponent";
import { SlideMenuBar } from "@/Common/Components/Sidebar/SlideMenuBar";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { NumberHelper } from "@/Common/Helpers/NumberHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { VigilanceAlertsData } from "@/Common/Models/VigilanceAlertsData";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { resolveMedication } from "@/Patient/Api/MedicationApiClient";
import { MedicalReactionsView } from "@/Patient/Components/MedicalItems/MedicalReactionsView";
import { SummaryPanelButtons } from "@/Patient/Components/MedicalSummaryPanel/SummaryPanelButtons";
import { PatientPrescriptionAdjustMedicationPopup } from "@/Patient/Components/Medication/PatientPrescriptionAdjustMedicationPopup";
import { MedicationListType, PatientPrescriptionTab } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { MedicationSearchListItem } from "@/Patient/Components/Medication/SearchMedication";
import { VigilanceQueryViewerPopupHelper } from "@/Patient/Helpers/VigilanceQueryViewerPopupHelper";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";
import { PatientPrescriptionAdjustMedicationPopupModel } from "@/Patient/Models/PatientPrescriptionAdjustMedicationPopupModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { DsqMedicationsResponse } from "../Api/Dto/DsqMedicationsResponse";
import { createNewPrescriptionRenewalClaim, fetchDsqMedicationSummary, fetchVigilanceAlerts, fetchVigilanceQueryViewerData } from "../Api/PrescriptionRenewalsClient";
import { IPrescriptionRenewalsViewContextValueModel } from "../Models/IPrescriptionRenewalsViewContextValueModel";
import { PrescriptionRenewalMedications } from "../Models/PrescriptionRenewalMedications";
import { PrescriptionRenewalsViewContext } from "./PrescriptionRenewalsView";
import { PrescriptionRenewalsViewEditMedicationPopup } from "./PrescriptionRenewalsViewEditMedicationPopup";
import { PrescriptionRenewalsViewRenewMedicationTable } from "./PrescriptionRenewalsViewRenewMedicationTable";

import styles from "./PrescriptionRenewalsViewRenewMedication.module.scss";

interface IMatchRelatedMedications {
    activeMedications: MedicationModel[];
    prescriptionMedications: MedicationModel[];
    relatedMedicationsData: PrescriptionRenewalMedications[];
}

interface IPrescriptionRenewalsViewRenewMedicationProps {
    prescriptionRenewalId: string;
    patientSummary: IPatientMedicalSummaryModel,
    patientProfile: Patient;
}

function PrescriptionRenewalsViewRenewMedication(props: IPrescriptionRenewalsViewRenewMedicationProps): JSX.Element {
    const { getText } = useTranslation();

    const prescriptionRenewalsViewContextValue: IPrescriptionRenewalsViewContextValueModel = useContext(PrescriptionRenewalsViewContext);
    const [isPharmaNetLoginOpen, setPharmaNetLoginOpen] = useState<boolean>(false);
    const [summaryState, setSummaryState] = useState<IPatientMedicalSummaryModel>(null);
    const [isClickableTextPanelOpen, setIsClickableTextPanelOpen] = useState<boolean>(false);
    const [medicationModelToAdjust, setMedicationModelToAdjust] = useState<MedicationModel>(null);
    const [globalRenewalsNumber, setGlobalRenewalsNumber] = useState<number>(0);
    const [medicationModelToAdd, setMedicationModelToAdd] = useState<MedicationModel>(null);
    const [currentPrescriptionRenewalId, setCurrentPrescriptionRenewalId] = useState<string>(null);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState<boolean>(prescriptionRenewalsStore.isPrescriptionRenewalsViewRenewMedicationHeaderExpanded ?? true);

    const openSecondScreen = (): void => {
        SecondScreenHelper.openWindow({ patientId: props.patientSummary?.get("PatientContactId") });
    };

    const hasPharmaNetAccess = (): boolean => cacheStore.hasPharmaNetAccess();

    const getDsqMedications = (): void => {
        if (!prescriptionRenewalsViewContextValue.prescriptionState.get("isDSQLoading")) {
            prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("isDSQLoading", true));
            fetchDsqMedicationSummary(summaryState.get("PatientContactId"), (success: boolean, dsqMedicationsResponse: DsqMedicationsResponse) => {
                prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("isDSQLoading", false));

                if (success) {
                    let activeMedications = dsqMedicationsResponse?.myleDSQMedications?.map(x => MedicationModel.FromMyleDSQMedicationItem(x));
                    let prescriptionMedications = prescriptionRenewalsViewContextValue?.prescriptionState?.get("MedicationModels")?.toArray() ?? [];

                    const matchedActiveMedications = matchActiveMedications(activeMedications, prescriptionMedications, {});

                    if (matchedActiveMedications) {
                        ({ activeMedications, prescriptionMedications } = matchedActiveMedications);
                    }

                    prescriptionRenewalsViewContextValue?.setPrescriptionState(p => p.set("MedicationModels", Immutable.List(prescriptionMedications)));
                    prescriptionRenewalsViewContextValue?.setPrescriptionState(p => p
                        .set("DSQMedicationModels", activeMedications.toImmutable())
                        .set("selectedMedicationList", MedicationListType.DSQ)
                    );
                }
            });
        }
    };

    const toggleSelectedMedicationList = (selectedMedicationListType: MedicationListType): void => {
        if (selectedMedicationListType == MedicationListType.DSQ) {
            if (prescriptionRenewalsViewContextValue.prescriptionState.get("DSQMedicationModels"))
                prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("selectedMedicationList", MedicationListType.DSQ));
            else
                getDsqMedications();
        }
        else {
            prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("selectedMedicationList", selectedMedicationListType));
        }
    };

    const getVigilanceAlerts = async (medicationModels: Immutable.List<MedicationModel>, eventId: string): Promise<VigilanceAlertsData> => {
        if (!medicationModels || !props.patientSummary?.get("PatientContactId"))
            return;

        const newDins = medicationModels
            .filter(p => p.Model.PrescriptionTab_PendingStatus == null)
            .map(p => p.Model.DIN)
            .toArray();
        const interruptedDins = medicationModels
            .filter(p => p.Model.PrescriptionTab_PendingStatus != null)
            .map(p => p.Model.DIN)
            .toArray();

        const vigilanceResponse = await fetchVigilanceAlerts(props.patientSummary.get("PatientContactId"), newDins, interruptedDins, eventId);
        return VigilanceAlertsData.fromResponseModel(vigilanceResponse);
    };

    const updateMedicationModels = async (medicationModels: Immutable.List<MedicationModel>): Promise<void> => {
        const eventId: string = GuidHelper.NewGuid();
        prescriptionRenewalsViewContextValue.setPrescriptionState(p => p
            .set("LastVigilanceAlertEvent", eventId)
            .set("VigilanceNumberOfAlerts", null)
            .set("VigilanceSevereAlertLevel", null)
            .set("VigilanceLastDatabaseUpdateDate", null)
            .set("MedicationModels", medicationModels)
        );

        const vigilanceData = await getVigilanceAlerts(medicationModels, eventId);

        if (vigilanceData?.eventId === eventId) {
            prescriptionRenewalsViewContextValue.setPrescriptionState(p => p
                .set("LastVigilanceAlertEvent", vigilanceData.eventId)
                .set("VigilanceNumberOfAlerts", vigilanceData.numberOfAlerts)
                .set("VigilanceSevereAlertLevel", vigilanceData.severeAlertLevel)
                .set("VigilanceLastDatabaseUpdateDate", vigilanceData.vigilanceLastDatabaseUpdateDate)
            );
        }
    };

    const addMedicationToMedicationModels = (medicationId: string, currentMedicationModels: Immutable.List<MedicationModel> = null): Immutable.List<MedicationModel> => {
        const activeSummaryMeds = summaryState
            .get("MedicalItemsMedicationAndPrescription")
            .filter(x => x.Model.SummaryDisplayStatus == 1 || x.Model.SummaryDisplayStatus == 2);

        const originalMed = summaryState
            .get("MedicalItemsMedicationAndPrescription")
            .filter(x => x.Model.Id == medicationId).map(y => y)
            .first();

        const medicationModels = currentMedicationModels ?? prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels");
        if (!originalMed && prescriptionRenewalsViewContextValue.prescriptionState.get("selectedMedicationList") === MedicationListType.DSQ) {
            return addDsqMedicationToMedicationModels(medicationId, medicationModels, activeSummaryMeds);
        }

        const renewedMed = originalMed.Renew();
        renewedMed.RecalculateRefillEndDate();

        if (renewedMed.Model.isDSQMed_NotImported == true) {
            renewedMed.Model.DSQRenew_MyleActiveMedicationIdsConflic = activeSummaryMeds
                .filter(activeMed => activeMed.Model.CodeGen == originalMed.Model.CodeGen)
                .map(activemed => activemed.Model.Id)
                .toArray();
        }

        activeSummaryMeds.forEach(x => x.IsInEditMode = false);
        medicationModels.forEach(x => {
            x.EMRTemplateSortIndexSet(x.Model.EMRTemplateSortIndex + 1);
        });
        renewedMed.EMRTemplateSortIndexSet(0);
        mutateMedication(medicationId, (medication => {
            medication.Model.PrescriptionTab_PendingStatus = null;
            medication.Model.PrescriptionTab_IsRenew = true;
        }));

        return addNewMedicationModel(medicationModels, renewedMed);
    };

    const addDsqMedicationToMedicationModels = (medicationId: string, medicationModels: Immutable.List<MedicationModel> = null, activeSummaryMeds: Immutable.Iterable<number, MedicationModel>): Immutable.List<MedicationModel> => {
        const originalMed = prescriptionRenewalsViewContextValue.prescriptionState
            .get("DSQMedicationModels")
            .filter(x => x.Model.Id == medicationId).map(y => y)
            .first();

        if (!originalMed)
            return;

        if (originalMed.Model.PrescriptionTab_IsRenew != true && originalMed.Model.PrescriptionTab_PendingStatus == null) {
            const renewedModel = originalMed.Renew();
            renewedModel.Model.DSQRenew_MyleActiveMedicationIdsConflic = activeSummaryMeds
                .filter(activeMed => activeMed.Model.CodeGen == renewedModel.Model.CodeGen)
                .map(activemed => activemed.Model.Id).toArray();

            medicationModels.forEach(x => {
                x.EMRTemplateSortIndexSet(x.Model.EMRTemplateSortIndex + 1);
            });
            renewedModel.EMRTemplateSortIndexSet(0);
            mutateMedication(medicationId, (medication => {
                medication.Model.PrescriptionTab_PendingStatus = null;
                medication.Model.PrescriptionTab_IsRenew = true;
            }), true);
            return addNewMedicationModel(medicationModels, renewedModel);
        }
    };

    const addNewMedicationModel = (medicationModels: Immutable.List<MedicationModel>, newMedicationModel: MedicationModel): Immutable.List<MedicationModel> => {
        const newMedicationModels = medicationModels.reverse().toList().push(newMedicationModel).reverse().toList();
        return newMedicationModels;
    };

    const getRenewedMedicationsIds = (renewedMedications: Readonly<Immutable.List<MedicationModel>>): string[] => renewedMedications?.toArray().map(medication => medication.Model.Id) ?? [];

    const renewAllMedications = (): void => {
        let currentMeds = null;
        if (prescriptionRenewalsViewContextValue.prescriptionState.get("selectedMedicationList") === MedicationListType.DSQ) {
            currentMeds = prescriptionRenewalsViewContextValue.prescriptionState.get("DSQMedicationModels")
                .filter(medication => !medication.Model.PrescriptionTab_IsRenew && !medication.Model.PrescriptionTab_PendingStatus);
        }
        else {
            currentMeds = summaryState
                .get("MedicalItemsMedicationAndPrescription")
                .filter(medication =>
                    (medication.Model.SummaryDisplayStatus == 1 || medication.Model.SummaryDisplayStatus == 2) &&
                        !medication.Model.PrescriptionTab_IsRenew &&
                        !medication.Model.PrescriptionTab_PendingStatus
                );
        }

        if (!currentMeds?.count())
            return;

        let newMedicationModels: Immutable.List<MedicationModel> = null;
        currentMeds.forEach(medication => {
            newMedicationModels = addMedicationToMedicationModels(medication.Model.Id, newMedicationModels);
        });

        mutateMedicationModel(newMedicationModels);
    };

    const renewMedication = (medicationId: string): void => {
        const newMedicationModels = addMedicationToMedicationModels(medicationId);
        mutateMedicationModel(newMedicationModels);
    };

    const mutateMedicationModel = (newMedicationModels: Immutable.List<MedicationModel>, updateRelatedMedications: boolean = true): void => {
        updateMedicationModels(newMedicationModels);
        if (updateRelatedMedications)
            prescriptionRenewalsViewContextValue.addRelatedPrescriptionRenewalMedications(getRenewedMedicationsIds(newMedicationModels));

        prescriptionRenewalsViewContextValue.setIsDirty(true);
    };

    const mutateMedication = (medicationId: string, mutation: (medicationModel: MedicationModel) => void, isDsq: boolean = false): void => {
        const medicationModels = isDsq ?
            prescriptionRenewalsViewContextValue?.prescriptionState?.get("DSQMedicationModels") :
            summaryState.get("MedicalItemsMedicationAndPrescription");

        const currentMedication = medicationModels?.find(medication => medication.Model.Id === medicationId);
        if (!currentMedication)
            return;

        mutation(currentMedication);
        if (isDsq)
            prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("DSQMedicationModels", medicationModels));
        else
            setSummaryState(summaryState.set("MedicalItemsMedicationAndPrescription", medicationModels));
    };

    const removeMedication = (currentMedication: MedicationModel): void => {
        mutateMedication(currentMedication.Model?.OriginalMedicationId, (medication: MedicationModel) => {
            medication.Model.PrescriptionTab_PendingStatus = null;
            medication.Model.PrescriptionTab_IsRenew = false;
        });

        mutateMedication(currentMedication.Model?.OriginalMedicationId, (medication: MedicationModel) => {
            medication.Model.PrescriptionTab_PendingStatus = null;
            medication.Model.PrescriptionTab_IsRenew = false;
        }, true);

        mutateMedication(currentMedication.Model?.Id, (medication: MedicationModel) => {
            medication.Model.PrescriptionTab_PendingStatus = null;
            medication.Model.PrescriptionTab_IsRenew = false;
        });

        const filteredModels = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels").filter(medication => currentMedication.Model.Id !== medication.Model.Id).toList();
        updateMedicationModels(filteredModels);
        prescriptionRenewalsViewContextValue.removeRelatedPrescriptionRenewalMedications(getRenewedMedicationsIds(filteredModels));
        prescriptionRenewalsViewContextValue.setIsDirty(true);
    };

    const openVigilancePopup = async (): Promise<void> => {
        const medicationModels = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels");
        const newDrugIdNumbers = medicationModels
            .filter(p => p.Model.PrescriptionTab_PendingStatus == null)
            .map(p => p.Model.DIN)
            .toArray();
        const interuptedDrugIdNumbers = medicationModels
            .filter(p => p.Model.PrescriptionTab_PendingStatus != null)
            .map(p => p.Model.DIN)
            .toArray();

        const vigilanceData = await fetchVigilanceQueryViewerData(props.patientSummary.get("PatientContactId"), newDrugIdNumbers, interuptedDrugIdNumbers);
        const vigilanceUrl = cacheStore.ClinicSettingsServices.VigilanceServiceV2;
        const token = await VigilanceTokenClient.getVigilanceTokenAsync();
        VigilanceQueryViewerPopupHelper.openVigilanceViewerPopup(vigilanceUrl, vigilanceData.PatientData, token);
    };

    const currentEmployeeBillingProvider = useMemo(
        () => sessionStore.getCurrentEmployee().getBillingProviderResolved(),
        []
    );

    const createNewClaim = useCallback(
        () => createNewPrescriptionRenewalClaim(props.prescriptionRenewalId, currentEmployeeBillingProvider),
        [props.prescriptionRenewalId, currentEmployeeBillingProvider]
    );

    const shouldDisplayPaymentRequestLink: boolean = useMemo(
        () => BillingProviderHelper.getBillingProviderSupportsPrescriptionRenewal(currentEmployeeBillingProvider) && sessionStore.getCurrentEmployee().isActiveSiteBC(),
        [currentEmployeeBillingProvider]
    );

    const onSortMedications = (sortedMedications: MedicationModel[]): void => {
        const invalidLength: number = -1;
        const currentTotalMedications: number = prescriptionRenewalsViewContextValue.prescriptionState?.get("MedicationModels")?.count() ?? invalidLength;

        if (currentTotalMedications === sortedMedications?.length) {
            prescriptionRenewalsViewContextValue.setPrescriptionState(prev => prev.set("MedicationModels", Immutable.List(sortedMedications)));
        }
    };

    const onAdjustMedicationPopupOpen = (medicationId: string): void => {
        let medicationModel: MedicationModel = null;
        if (prescriptionRenewalsViewContextValue.prescriptionState.get("selectedMedicationList") === MedicationListType.DSQ) {
            medicationModel = prescriptionRenewalsViewContextValue.prescriptionState.get("DSQMedicationModels")
                .filter(medication => medication.Model.Id === medicationId).first()?.Clone(false, false, true);
        }
        else {
            medicationModel = summaryState.get("MedicalItemsMedicationAndPrescription")
                ?.filter(p => p.Model.Id === medicationId).first()?.Clone(false, false, true) ?? null;
        }

        setMedicationModelToAdjust(medicationModel);
    };

    const onAdjustMedication = (prescriptionAdjustMedicationModel: PatientPrescriptionAdjustMedicationPopupModel): void => {
        const { baseMed, newMed, selectedCode, ceaseDate, ceaseReason, discontinueReasonLookupId } = prescriptionAdjustMedicationModel;
        if (baseMed != null) {
            mutateMedication(baseMed.Model.Id, (medication: MedicationModel) => {
                medication.Model.PrescriptionTab_PendingStatus = selectedCode;
                medication.Model.PrescriptionTab_IsRenew = false;
            }, prescriptionRenewalsViewContextValue.prescriptionState.get("selectedMedicationList") === MedicationListType.DSQ);

            const ceaseEmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;
            const ceaseEncounterId = null; // patientStore.PatientPrescriptionTabModels[patientId].get("EncounterId");
            const adjustedMedication = MedicationModel.setAdjustMedication(baseMed, selectedCode, ceaseDate, ceaseReason, discontinueReasonLookupId, ceaseEmployeeId, ceaseEncounterId);
            adjustedMedication.Model.Id = GuidHelper.NewGuid();
            adjustedMedication.Model.MedicalItemId = adjustedMedication.Model.Id;
            adjustedMedication.Model.OriginalMedicationId = baseMed.Model.Id;
            adjustedMedication.Model.PrescriptionStatusInt = parseInt(selectedCode);
            const currentMedicationModels = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels");
            let updatedMedicationModels = null;

            if (newMed != null) {
                newMed.PrescriptionTab_OriginalMedication = adjustedMedication;
                newMed.Model.OriginalMedicationId = baseMed.Model.Id;
                newMed.Model.PrescriptionStatusInt = parseInt(selectedCode);
                updatedMedicationModels = addNewMedicationModel(currentMedicationModels, newMed);
                updateRelatedMedication(newMed.Model.Id, parseInt(selectedCode), ceaseDate, ceaseReason, discontinueReasonLookupId);
            }
            else {
                updatedMedicationModels = addNewMedicationModel(currentMedicationModels, adjustedMedication);
                updateRelatedMedication(adjustedMedication.Model.Id, parseInt(selectedCode), ceaseDate, ceaseReason, discontinueReasonLookupId);
            }

            mutateMedicationModel(updatedMedicationModels, false);
            setMedicationModelToAdjust(null);
        }
    };

    const updateRelatedMedication = (id: Guid | string, selectedCode: number, ceaseDate: Date, ceaseReason: string, discontinueReasonLookupId: string | null): void => {
        let matchedMedication = prescriptionRenewalsViewContextValue.relatedPrescriptionRenewalMedications?.firstOrDefault(p => p.medicationId === id);
        if (!matchedMedication) {
            matchedMedication = new PrescriptionRenewalMedications({});
            matchedMedication.medicationId = id;
        }
        matchedMedication.pendingStatus = selectedCode;
        matchedMedication.ceaseDate = ceaseDate;
        matchedMedication.ceaseReason = ceaseReason;
        matchedMedication.discontinueReasonLookupId = discontinueReasonLookupId;
        const newRelatedMedications = prescriptionRenewalsViewContextValue.relatedPrescriptionRenewalMedications
            .filter(p => p.medicationId != id);

        newRelatedMedications.push(matchedMedication);
        prescriptionRenewalsViewContextValue.setRelatedPrescriptionRenewalMedications(newRelatedMedications);
    };

    const onAddMedicationPopupOpen = (): void => {
        setMedicationModelToAdd(MedicationModel.FromDefaultModel(true, false));
    };

    const onAddMedicationPopupClose = (): void => {
        setMedicationModelToAdd(null);
    };

    const addSearchedMedication = (item: MedicationSearchListItem): void => {
        if (item.isFavorite) {
            const favoriteItems: MedicationModel[] = item.favoriteItem;

            MedicationModel.sortMedicationModelsByEMRTemplateSortIndex(favoriteItems);

            let newMedicationModels = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels");
            favoriteItems.forEach(p => {
                const tmpMed = p.Clone(true, true, false, true);
                tmpMed.Model.NumberOfRefill = globalRenewalsNumber;

                newMedicationModels = addNewMedicationModel(newMedicationModels, tmpMed);
            });
            mutateMedicationModel(newMedicationModels);
        }
        else {
            const medicationResponse = item.responseItem;
            const medicationModel = MedicationModel.FromDefaultModel(false, false);

            medicationModel.FromResponseLightUpdate(medicationResponse, false);
            medicationModel.Model.NumberOfRefill = globalRenewalsNumber;

            const newMedicationModels = addNewMedicationModel(prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels"), medicationModel);
            mutateMedicationModel(newMedicationModels);

            if (medicationResponse.IsLightDTO) {
                medicationResponse.Id = medicationModel.Model.Id;
                resolveMedication(medicationResponse, medicationResponse.Id, (response: MedicationResponse) => {
                    medicationModel.FromResponseFullUpdate(response);

                    if (globalRenewalsNumber) {
                        medicationModel.Model.NumberOfRefill = globalRenewalsNumber;
                    }
                });
            }
            else {
                medicationModel.FromResponseFullUpdate(medicationResponse);
            }
        }
    };

    const applyGlobalRenewalsNumberToAllMedications = (renewalsNumber: number): void => {
        const currentMedicationModels: MedicationModel[] = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels").toArray();
        const updatedMedicationModels: MedicationModel[] = [];

        for (const currentMedicationModel of currentMedicationModels) {
            if (!currentMedicationModel.IsNarcotic()) {
                currentMedicationModel.Model.NumberOfRefill = renewalsNumber;
                currentMedicationModel.RecalculateTotalDuration();
            }
            updatedMedicationModels.push(currentMedicationModel.Clone(false, false, true));
        }

        prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("MedicationModels", Immutable.List(updatedMedicationModels)));
        prescriptionRenewalsViewContextValue.setIsDirty(true);
    };

    useEffect(() => {
        const setInitialVigilanceAlerts = async (): Promise<void> => {
            if (!props.patientSummary?.get("PatientContactId"))
                return;

            const medicationModels = prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels");
            const newDrugIdentificationNumbers = medicationModels
                .filter(p => p.Model.PrescriptionTab_PendingStatus == null)
                .map(p => p.Model.DIN)
                .toArray();
            const interruptedDrugIdentificationNumbers = medicationModels
                .filter(p => p.Model.PrescriptionTab_PendingStatus != null)
                .map(p => p.Model.DIN)
                .toArray();

            const vigilanceResponse = await fetchVigilanceAlerts(props.patientSummary.get("PatientContactId"), newDrugIdentificationNumbers, interruptedDrugIdentificationNumbers, GuidHelper.NewGuid());
            const vigilanceData = VigilanceAlertsData.fromResponseModel(vigilanceResponse);

            prescriptionRenewalsViewContextValue.setPrescriptionState(prescriptionState => prescriptionState
                .set("LastVigilanceAlertEvent", vigilanceData.eventId)
                .set("VigilanceNumberOfAlerts", vigilanceData.numberOfAlerts)
                .set("VigilanceSevereAlertLevel", vigilanceData.severeAlertLevel)
                .set("VigilanceLastDatabaseUpdateDate", vigilanceData.vigilanceLastDatabaseUpdateDate)
            );
        };

        setInitialVigilanceAlerts();
    }, [props.patientSummary]);

    useEffect(() => {
        let activeMedications = props.patientSummary?.get("MedicalItemsMedicationAndPrescription")?.toArray();
        let prescriptionMedications = prescriptionRenewalsViewContextValue?.prescriptionState?.get("MedicationModels")?.toArray() ?? [];
        let relatedMedicationsData = prescriptionRenewalsViewContextValue?.relatedPrescriptionRenewalMedications;
        const relatedMedicationsDictionary: { [prescriptionRenewalId: string]: PrescriptionRenewalMedications } = {};
        const matchedData = matchRelatedMedications(activeMedications, prescriptionMedications, relatedMedicationsData, relatedMedicationsDictionary);
        if (matchedData) {
            ({ activeMedications, prescriptionMedications, relatedMedicationsData } = matchedData);
            prescriptionRenewalsViewContextValue.setRelatedPrescriptionRenewalMedications(relatedMedicationsData);
        }

        const matchedActiveMedications = matchActiveMedications(activeMedications, prescriptionMedications, relatedMedicationsDictionary);
        if (matchedActiveMedications) {
            ({ activeMedications, prescriptionMedications } = matchedActiveMedications);
        }
        setSummaryState(props.patientSummary?.set("MedicalItemsMedicationAndPrescription", activeMedications.toImmutable()));
        prescriptionRenewalsViewContextValue?.setPrescriptionState(p => p.set("MedicationModels", Immutable.List(prescriptionMedications)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.patientSummary, props.prescriptionRenewalId, prescriptionRenewalsViewContextValue.relatedPrescriptionRenewalMedications]);

    useEffect(() => {
        if (currentPrescriptionRenewalId != props.prescriptionRenewalId) {
            setCurrentPrescriptionRenewalId(props.prescriptionRenewalId);
            prescriptionRenewalsViewContextValue
                .setPrescriptionState(p => p.set("selectedMedicationList", MedicationListType.Active).set("DSQMedicationModels", null));
        }
    }, [currentPrescriptionRenewalId, prescriptionRenewalsViewContextValue, props.prescriptionRenewalId]);

    useEffect(() => {
        sessionStore.dispatch(
            LocalOperations.PrescriptionRenewal.SavePrescriptionRenewalsViewTogglerSession,
            {
                isPrescriptionRenewalsViewRenewMedicationHeaderExpanded: isHeaderExpanded
            }
        );
    }, [isHeaderExpanded]);

    const matchRelatedMedications = (activeMedications: MedicationModel[], prescriptionMedications: MedicationModel[], relatedMedicationsData: PrescriptionRenewalMedications[], relatedMedicationsDictionary: { [prescriptionRenewalId: string]: PrescriptionRenewalMedications }): IMatchRelatedMedications | null => {
        if (relatedMedicationsData?.length && activeMedications?.length) {
            relatedMedicationsData.forEach(relatedMedication => {
                const matchedPrescriptionMedication = prescriptionMedications.firstOrDefault(prescriptionMedication => prescriptionMedication.Model.Id === relatedMedication.medicationId);
                if (!matchedPrescriptionMedication) {
                    const matchedMedication = activeMedications.firstOrDefault(activeMedication => activeMedication.Model.Id === relatedMedication.medicationId);
                    if (!matchedMedication)
                        return;

                    const clonedMedication = matchedMedication.Clone(true);
                    clonedMedication.Model.PrescriptionStatusInt = relatedMedication.pendingStatus;
                    clonedMedication.Model.PrescriptionTab_PendingStatus = relatedMedication.pendingStatus?.toString();
                    clonedMedication.Model.CeaseDate = relatedMedication.ceaseDate;
                    clonedMedication.Model.CeaseReason = relatedMedication.ceaseReason;
                    clonedMedication.Model.DiscontinueReasonLookupId = relatedMedication.discontinueReasonLookupId;
                    clonedMedication.Model.OriginalMedicationId = relatedMedication.medicationId.toString();
                    prescriptionMedications = prescriptionMedications.concat(clonedMedication);
                    relatedMedication.medicationId = clonedMedication.Model.Id;
                }
                else {
                    relatedMedicationsDictionary[matchedPrescriptionMedication.Model.OriginalMedicationId] = relatedMedication;
                    if (!matchedPrescriptionMedication.Model.PrescriptionStatusInt && relatedMedication.pendingStatus) {
                        matchedPrescriptionMedication.Model.PrescriptionStatusInt = relatedMedication.pendingStatus;
                        matchedPrescriptionMedication.Model.CeaseDate = null;
                        matchedPrescriptionMedication.Model.CeaseReason = null;
                        matchedPrescriptionMedication.Model.DiscontinueReasonLookupId = null;
                        matchedPrescriptionMedication.RecalculateTotalDuration();
                    }
                }
            });

            return { activeMedications, prescriptionMedications, relatedMedicationsData };
        }

        return null;
    };

    const matchActiveMedications = (activeMedications: MedicationModel[], prescriptionMedications: MedicationModel[], relatedMedicationsDictionary: { [prescriptionRenewalId: string]: PrescriptionRenewalMedications }): { activeMedications: MedicationModel[], prescriptionMedications: MedicationModel[] } | null => {
        if (prescriptionMedications && activeMedications) {
            activeMedications?.forEach(activeMedication => {
                const matchedMedication = prescriptionMedications.firstOrDefault(prescriptionMedication => prescriptionMedication.Model.OriginalMedicationId === activeMedication.Model.Id);
                if (matchedMedication) {
                    activeMedication.Model.PrescriptionTab_IsRenew = !matchedMedication.Model.PrescriptionStatusInt;
                    if (matchedMedication.Model.PrescriptionStatusInt) {
                        activeMedication.Model.PrescriptionStatusInt = matchedMedication.Model.PrescriptionStatusInt;
                        activeMedication.Model.PrescriptionTab_PendingStatus = matchedMedication.Model.PrescriptionStatusInt.toString();
                    }
                    if (relatedMedicationsDictionary[activeMedication.Model.Id]) {
                        activeMedication.Model.CeaseDate = relatedMedicationsDictionary[activeMedication.Model.Id].ceaseDate;
                        activeMedication.Model.CeaseReason = relatedMedicationsDictionary[activeMedication.Model.Id].ceaseReason;
                        activeMedication.Model.DiscontinueReasonLookupId = relatedMedicationsDictionary[activeMedication.Model.Id].discontinueReasonLookupId;
                    }
                    if (matchedMedication.PrescriptionTab_OriginalMedication?.Model) {
                        activeMedication.Model.CeaseEmployeeId = matchedMedication.PrescriptionTab_OriginalMedication.Model.CeaseEmployeeId;
                        activeMedication.Model.CeaseDate = matchedMedication.PrescriptionTab_OriginalMedication.Model.CeaseDate;
                        activeMedication.Model.CeaseReason = matchedMedication.PrescriptionTab_OriginalMedication.Model.CeaseReason;
                        activeMedication.Model.DiscontinueReasonLookupId = matchedMedication.PrescriptionTab_OriginalMedication.Model.DiscontinueReasonLookupId;
                    }
                    matchedMedication.PrescriptionTab_OriginalMedication = activeMedication;
                }
            });

            return { activeMedications, prescriptionMedications };
        }

        return null;
    };

    const closeClickableTextPanel = (): void => {
        if (isClickableTextPanelOpen) setIsClickableTextPanelOpen(false);
    };

    const { isPrescriptionRenewalCompleted } = prescriptionRenewalsViewContextValue;
    const disabledLinkClassNameWhenCompleted = isPrescriptionRenewalCompleted ? styles.disabledLink : "";

    return (
        <div className={styles.container} data-cy="rxrenewalview-medication" onClick={() => closeClickableTextPanel()}>
            <div className={`${styles.header} ${isHeaderExpanded ? styles.expanded : "" }`}>
                <MedicalReactionsView reactions={props.patientSummary?.get("MedicalItemsMedicalReaction").toArray() ?? []} />
                <GeneralInstructionsComponent
                    generalInstructions={prescriptionRenewalsViewContextValue?.prescriptionRenewalNote}
                    isClickableTextPanelOpen={isClickableTextPanelOpen}
                    onTextChangeCallback={(value: string) => {
                        prescriptionRenewalsViewContextValue.setPrescriptionRenewalNote(value);
                        prescriptionRenewalsViewContextValue.setIsDirty(true);
                    }}
                    onCloseClickableText={() => setIsClickableTextPanelOpen(false)}
                    onOpenClickableText={() => setIsClickableTextPanelOpen(true)}
                    isReadonly={isPrescriptionRenewalCompleted || !!prescriptionRenewalsViewContextValue.prescriptionState?.get("MedicationModels")?.isEmpty()}
                />
                <div className={styles.linkContainer}>
                    {cacheStore.ClinicSettings.EnableSecondScreen && sessionStore.getCurrentEmployee().Type !== EmployeeType.Assistant && (
                        <div className={styles.summaryPanelButtonContainer} data-cy="prescriptionrenewalview-medication-viewsummary">
                            <SummaryPanelButtons
                                isMedicalSummaryPanelOpen={false}
                                onPopoutClick={() => openSecondScreen()}
                                onMedicalSummaryLinkClick={() => openSecondScreen()}
                                cssClass="noteToggleContainer"
                                hideOpenSummaryPanelLink
                            />
                        </div>
                    )}
                    {shouldDisplayPaymentRequestLink && (
                        <div>
                            <a
                                onClick={() => {
                                    if (isPrescriptionRenewalCompleted) {
                                        return;
                                    }

                                    createNewClaim();
                                }}
                                className={disabledLinkClassNameWhenCompleted}
                            >{getText("PaymentRequestLabel")}
                            </a>
                        </div>
                    )}
                    {hasPharmaNetAccess() && (
                        <div data-cy="prescriptionrenewalview-medication-pharmanet">
                            <a
                                onClick={() => {
                                    if (isPrescriptionRenewalCompleted) {
                                        return;
                                    }

                                    setPharmaNetLoginOpen(true);
                                }}
                                className={disabledLinkClassNameWhenCompleted}
                            >
                                {getText("PharmaNet")}
                            </a>
                        </div>
                    )}
                </div>
            </div>
            <div
                className={styles.horizontalBarContainer}
                onClick={() => setIsHeaderExpanded(p => !p)}
                data-cy="renew-medication-bar-container"
            >
                <SlideMenuBar />
            </div>
            {summaryState && (
                <PatientPrescriptionTab
                    MedicalSummaryModel={summaryState}
                    PatientContactId={props.patientProfile?.IndividualResponse.Contact_id}
                    Model={prescriptionRenewalsViewContextValue.prescriptionState}
                    encounterSiteId={sessionStore.getCurrentEmployee().getActiveSite().Model.Id}
                    onToggleSelectedMedicationList={(selectedMedicationListType: MedicationListType) => { toggleSelectedMedicationList(selectedMedicationListType); }}
                    onAdjustMedicationPopupOpen={(medicationId: string) => onAdjustMedicationPopupOpen(medicationId)}
                    onRenewMedication={medicationId => renewMedication(medicationId)}
                    onRenewAllMedications={() => renewAllMedications()}
                    onRemoveMedication={medicationId => removeMedication(medicationId)}
                    onAddEmptyMedication={() => onAddMedicationPopupOpen()}
                    onVigilanceSanteIconClick={() => openVigilancePopup()}
                    onExternalSearchMedicationTextChange={(medicationName: string) => {
                        prescriptionRenewalsViewContextValue.setPrescriptionState(p =>
                            p.set("SearchText", medicationName)
                                .set("SearchTextEventId", sessionStore.newId().toString())
                        );
                    }}
                    isAdjustButtonDisabled={isPrescriptionRenewalCompleted}
                    isRenewalButtonDisabled={isPrescriptionRenewalCompleted}
                    isSearchedMedicationDisabled={isPrescriptionRenewalCompleted}
                    isGlobalRenewalNumberDisabled={isPrescriptionRenewalCompleted}
                    isAddFavoriteDisabled={isPrescriptionRenewalCompleted}
                    isMedicationClickDisabled={isPrescriptionRenewalCompleted}
                    addFavoriteDisabledCssName={disabledLinkClassNameWhenCompleted}
                    emptyRxDisabledCssName={disabledLinkClassNameWhenCompleted}
                    onAddSelectedMedication={(item: MedicationSearchListItem) => addSearchedMedication(item)}
                    updateGlobalRenewalsNumberChange={(newText: string) => {
                        const parsedNumber = Number(newText);
                        const validatedNumber = NumberHelper.isValidNumber(parsedNumber) ? parsedNumber : 0;

                        setGlobalRenewalsNumber(validatedNumber);
                        applyGlobalRenewalsNumberToAllMedications(validatedNumber);
                    }}
                    toggleAddAsFavorite={() => {
                        if (!prescriptionRenewalsViewContextValue.prescriptionState)
                            return;

                        prescriptionRenewalsViewContextValue.setPrescriptionState(p => p.set("IsFavorite", !p.get("IsFavorite")));
                    }}
                    shouldHeaderContainGlobalRenewalNumber
                    showPatientDetails={false}
                >
                    <PrescriptionRenewalsViewRenewMedicationTable
                        medicationModel={prescriptionRenewalsViewContextValue.prescriptionState?.get("MedicationModels")?.toArray() ?? []}
                        relatedPrescriptionRenewalMedications={prescriptionRenewalsViewContextValue.relatedPrescriptionRenewalMedications}
                        shouldDisableAction={prescriptionRenewalsViewContextValue.isPrescriptionRenewalCompleted}
                        shouldDisableEvent={prescriptionRenewalsViewContextValue.isPrescriptionRenewalCompleted}
                        onSortMedications={sortedMedications => onSortMedications(sortedMedications)}
                        onRemoveMedication={medication => removeMedication(medication)}
                        onDenyClick={prescriptionRenewalsViewContextValue.onDenyClick}
                        onEditMedicationPopupOpen={prescriptionRenewalsViewContextValue.onEditMedicationPopupOpen}
                        onDropHandlerCallback={newMedicationModel => prescriptionRenewalsViewContextValue
                            .setPrescriptionState(p => p.set("MedicationModels", Immutable.List(newMedicationModel)))
                        }
                        hasActionColumn
                    />
                </PatientPrescriptionTab>
            )}
            {isPharmaNetLoginOpen && (
                <PharmaNetCredentials
                    onClose={() => setPharmaNetLoginOpen(false)}
                    patient={props.patientProfile}
                    context={PharmaNetContext.Patient}
                />
            )}
            {medicationModelToAdjust && (
                <PatientPrescriptionAdjustMedicationPopup
                    DefaultSelectedCode="1"
                    MedicationBeingAdjusted={medicationModelToAdjust}
                    onClosePopup={() => setMedicationModelToAdjust(null)}
                    onSavePopup={(prescriptionAdjustMedicationModel: PatientPrescriptionAdjustMedicationPopupModel) =>
                        onAdjustMedication(prescriptionAdjustMedicationModel)
                    }
                    manageOverlay
                    defaultDiscontinueReason={MedicationModel.getDefaultDiscontinueReason()}
                />
            )}
            {medicationModelToAdd && (
                <PrescriptionRenewalsViewEditMedicationPopup
                    title={getText("AddMedicationLabel")}
                    initialMedicationModel={medicationModelToAdd}
                    onCancel={() => onAddMedicationPopupClose()}
                    onSave={(updatedMedicationModel: MedicationModel) => {
                        const newMedicationModels = addNewMedicationModel(
                            prescriptionRenewalsViewContextValue.prescriptionState.get("MedicationModels"),
                            updatedMedicationModel
                        );

                        mutateMedicationModel(newMedicationModels);
                        onAddMedicationPopupClose();
                    }}
                />
            )}
        </div>
    );
}

export { PrescriptionRenewalsViewRenewMedication };
